/**
 * StepImport — Etapa de Importação de Backlog (F1-FE-3).
 *
 * Porta a tela `StepImport` do protótipo com dados reais:
 *   - Lista provedores de importação: GitHub Issues e Jira são reais;
 *     demais exibem "Em breve" (stub visual).
 *   - Conectar provedor → POST /projects/{id}/import-sources
 *   - Sincronizar → POST /projects/{id}/import-sources/{srcId}/sync
 *   - Lista de itens importados via GET /projects/{id}/imported-items
 *   - Resumo por tipo (ÉPICO, STORY, TASK, BUG)
 *
 * Só greenfield: não — esta etapa está em ambos os fluxos (ver JourneyContext).
 * É opcional: o usuário pode pular.
 *
 * Identidade: paleta C, estilos inline, pt-BR, a11y.
 * Standards: Clean, NFR (a11y), OWASP (sem dados sensíveis expostos).
 */
import { useState, useMemo } from 'react';
import {
  Github, Zap, ArrowRight, RefreshCw, CheckCircle2,
  Clock, AlertCircle, Package, Link,
} from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import { C } from '@/theme/palette';
import { Card, Btn, Badge, ForbiddenBanner, RateLimitBanner } from '@/components/ui';
import { LoadingSpinner, MutationError } from '@/components/ui/QueryState';
import { useJourney } from '../JourneyContext';
import { useApi } from '@/api/client';
import {
  useImportSources,
  useImportedItems,
  useConnectImportSource,
  importKeys,
} from '@/api/import';

// ─── Constantes de provedores ─────────────────────────────────────────────────

/**
 * Catálogo de provedores.
 * `real: true` → conectar/sync reais; `real: false` → stub "em breve".
 */
const PROVIDERS = [
  {
    id: 'github_issues',
    label: 'GitHub Issues',
    description: 'Importe issues, milestones e labels',
    icon: Github,
    color: C.neutral,
    real: true,
    credsHint: 'GitHub',
    configFields: [
      { key: 'owner', label: 'Owner', placeholder: 'ex.: nava-demo' },
      { key: 'repo',  label: 'Repositório', placeholder: 'ex.: core-api' },
    ],
  },
  {
    id: 'jira',
    label: 'Jira',
    description: 'Importe épicos, histórias, tarefas e bugs',
    icon: Zap,
    color: C.cyber,
    real: true,
    credsHint: 'Jira',
    configFields: [
      { key: 'project_key', label: 'Chave do projeto Jira', placeholder: 'ex.: NAVA' },
    ],
  },
  {
    id: 'azure_devops',
    label: 'Azure DevOps',
    description: 'Trabalho items e sprints',
    icon: Package,
    color: C.quantum,
    real: false,
  },
  {
    id: 'linear',
    label: 'Linear',
    description: 'Issues e projetos',
    icon: Link,
    color: C.neon,
    real: false,
  },
];

/** Cores por tipo de item de backlog. */
const TYPE_COLOR = {
  epic:    C.quantum,
  story:   C.cyber,
  task:    C.neutral,
  bug:     C.hot,
};

/** Labels pt-BR por tipo. */
const TYPE_LABEL = {
  epic:  'ÉPICO',
  story: 'STORY',
  task:  'TASK',
  bug:   'BUG',
};

// ─── Sub-componentes ──────────────────────────────────────────────────────────

function ProviderStatusBadge({ status }) {
  if (status === 'connected') {
    return (
      <Badge color={C.cyber} light>
        <CheckCircle2 size={10} style={{ marginRight: 4 }} aria-hidden="true" />
        CONECTADO
      </Badge>
    );
  }
  if (status === 'syncing') {
    return (
      <Badge color={C.digital} light>
        <RefreshCw size={10} style={{ marginRight: 4 }} aria-hidden="true" />
        SINCRONIZANDO
      </Badge>
    );
  }
  return (
    <Badge color={C.neutralMuted} light>NÃO CONECTADO</Badge>
  );
}

function ProviderCard({
  provider,
  source,       // dados vindos da API (ou null)
  onConnect,    // (config) => Promise
  onSync,
  connecting,
  syncing,
  connectError,
}) {
  const Icon   = provider.icon;
  const status = source?.status ?? 'disconnected';
  const isReal = provider.real;
  const configFields = provider.configFields ?? [];

  const [showForm, setShowForm] = useState(false);
  const [config,   setConfig]   = useState({});

  const openForm = () => {
    setConfig(Object.fromEntries(configFields.map((f) => [f.key, ''])));
    setShowForm(true);
  };
  const submit = async () => {
    try {
      await onConnect(config);
      setShowForm(false);
    } catch { /* erro exibido via connectError */ }
  };
  const canSubmit = configFields.every((f) => String(config[f.key] ?? '').trim());

  return (
    <div
      style={{
        backgroundColor: C.blackElev2,
        border: `1px solid ${C.blackBorder}`,
        borderRadius: 8,
        padding: '16px 20px',
      }}
      role="listitem"
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
        {/* Ícone */}
        <div
          style={{
            width: 40, height: 40, borderRadius: 8,
            backgroundColor: `${provider.color}18`, border: `1px solid ${provider.color}44`,
            display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
          }}
          aria-hidden="true"
        >
          <Icon size={20} style={{ color: provider.color }} />
        </div>

        {/* Info */}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2 }}>
            <span style={{ fontSize: 14, fontWeight: 600, color: C.neutral }}>{provider.label}</span>
            {!isReal && <Badge color={C.neutralMuted} light style={{ fontSize: 10 }}>EM BREVE</Badge>}
            {isReal && <ProviderStatusBadge status={status} />}
          </div>
          <span style={{ fontSize: 12, color: C.neutralDim }}>{provider.description}</span>
          {source?.last_sync_at && (
            <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4 }}>
              <Clock size={10} aria-hidden="true" />
              Última sync: {new Date(source.last_sync_at).toLocaleString('pt-BR')}
            </div>
          )}
        </div>

        {/* Ações */}
        <div style={{ flexShrink: 0, display: 'flex', gap: 8 }}>
          {!isReal ? (
            <Btn variant="ghost" size="sm" disabled aria-label={`${provider.label} em breve`}>
              Em breve
            </Btn>
          ) : status === 'connected' || status === 'syncing' ? (
            <Btn variant="ghost" size="sm" icon={RefreshCw} onClick={onSync}
              disabled={syncing || status === 'syncing'} aria-label={`Sincronizar ${provider.label}`}>
              {syncing ? 'Sincronizando...' : 'Sincronizar'}
            </Btn>
          ) : showForm ? null : (
            <Btn variant="outline" size="sm"
              onClick={() => (configFields.length ? openForm() : onConnect({}))}
              disabled={connecting} aria-label={`Conectar ${provider.label}`}>
              {connecting ? 'Conectando...' : 'Conectar'}
            </Btn>
          )}
        </div>
      </div>

      {/* Formulário de configuração (project_key / owner+repo) */}
      {showForm && (
        <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${C.blackBorder}` }}>
          <div style={{ fontSize: 11.5, color: C.neutralMuted, marginBottom: 10 }}>
            As credenciais do {provider.credsHint} são configuradas em{' '}
            <span style={{ color: C.cyber }}>Configurações → Integrações</span>. Aqui, só o destino do import.
          </div>
          {connectError && <MutationError error={connectError} />}
          <div className="flex items-end gap-3 flex-wrap">
            {configFields.map((f) => (
              <div key={f.key} style={{ flex: '1 1 160px' }}>
                <label htmlFor={`imp-${provider.id}-${f.key}`} style={{ display: 'block', fontSize: 12, color: C.neutralDim, marginBottom: 4 }}>
                  {f.label}
                </label>
                <input
                  id={`imp-${provider.id}-${f.key}`}
                  type="text"
                  value={config[f.key] ?? ''}
                  onChange={(e) => setConfig((s) => ({ ...s, [f.key]: e.target.value }))}
                  placeholder={f.placeholder || ''}
                  style={{
                    width: '100%', padding: '8px 10px', backgroundColor: C.black,
                    border: `1px solid ${C.blackBorder}`, borderRadius: 6,
                    color: C.neutral, fontSize: 13, boxSizing: 'border-box',
                  }}
                />
              </div>
            ))}
            <Btn variant="primary" size="sm" onClick={submit} disabled={!canSubmit || connecting}>
              {connecting ? 'Conectando...' : 'Conectar'}
            </Btn>
            <Btn variant="ghost" size="sm" onClick={() => setShowForm(false)}>Cancelar</Btn>
          </div>
        </div>
      )}
    </div>
  );
}

function ItemTypePill({ type, count }) {
  const color = TYPE_COLOR[type] ?? C.neutralDim;
  const label = TYPE_LABEL[type] ?? type.toUpperCase();
  return (
    <div
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 6,
        padding: '4px 10px',
        borderRadius: 20,
        backgroundColor: `${color}18`,
        border: `1px solid ${color}44`,
      }}
    >
      <span style={{ fontSize: 11, fontWeight: 600, color, letterSpacing: '0.04em' }}>
        {label}
      </span>
      <span
        style={{
          fontSize: 12,
          fontWeight: 700,
          color: C.neutral,
          backgroundColor: `${color}30`,
          borderRadius: 10,
          padding: '0 6px',
        }}
      >
        {count}
      </span>
    </div>
  );
}

function ImportedItemRow({ item }) {
  const color = TYPE_COLOR[item.type] ?? C.neutralDim;
  const label = TYPE_LABEL[item.type] ?? item.type?.toUpperCase() ?? '—';

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        padding: '10px 16px',
        borderBottom: `1px solid ${C.blackBorder}`,
      }}
    >
      <Badge color={color} light style={{ minWidth: 52, justifyContent: 'center' }}>
        {label}
      </Badge>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: 13,
            fontWeight: 500,
            color: C.neutral,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
          }}
        >
          {item.external_id ? `#${item.external_id} ` : ''}
          {item.title ?? item.name ?? '(sem título)'}
        </div>
        {item.points != null && (
          <div style={{ fontSize: 11, color: C.neutralMuted }}>{item.points} pts</div>
        )}
      </div>
      {item.provider && (
        <span style={{ fontSize: 11, color: C.neutralMuted, flexShrink: 0 }}>
          {item.provider}
        </span>
      )}
    </div>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function StepImport({ onNext, onBack }) {
  const { projectId } = useJourney();

  // ── Estado de conectando por provider ─────────────────────────────────────
  const [connectingProvider, setConnectingProvider] = useState(null);
  const [syncingSource, setSyncingSource]           = useState(null);

  // ── Queries ───────────────────────────────────────────────────────────────
  const {
    data: sourcesData,
    isLoading: sourcesLoading,
    error: sourcesError,
  } = useImportSources(projectId);

  const {
    data: itemsData,
    isLoading: itemsLoading,
    error: itemsError,
  } = useImportedItems(projectId, { limit: 50 });

  // ── Mutations ─────────────────────────────────────────────────────────────
  const connectMutation = useConnectImportSource(projectId);
  const api = useApi();
  const queryClient = useQueryClient();
  const [syncError, setSyncError] = useState(null);

  // ── Dados derivados ───────────────────────────────────────────────────────

  /** Mapeia provider_key → source da API */
  const sourcesByProvider = useMemo(() => {
    const sources = sourcesData?.items ?? sourcesData ?? [];
    return Object.fromEntries(
      (Array.isArray(sources) ? sources : []).map((s) => [s.provider_key ?? s.provider, s])
    );
  }, [sourcesData]);

  /** Itens importados (array) */
  const importedItems = useMemo(() => {
    const raw = itemsData?.items ?? itemsData;
    return Array.isArray(raw) ? raw : [];
  }, [itemsData]);

  /** Resumo por tipo */
  const typeSummary = useMemo(() => {
    const counts = {};
    for (const item of importedItems) {
      const t = item.type ?? 'task';
      counts[t] = (counts[t] ?? 0) + 1;
    }
    return counts;
  }, [importedItems]);

  const totalItems = importedItems.length;

  // ── Handlers ──────────────────────────────────────────────────────────────

  async function handleConnect(providerId, config = {}) {
    if (!projectId) return;
    const prov = PROVIDERS.find((p) => p.id === providerId);
    setConnectingProvider(providerId);
    try {
      // Contrato do backend (wizard): { provider_key, display_name, config }.
      // As credenciais (token/email/domínio) vêm do cofre (Integrações); aqui só
      // vão os metadados não-sensíveis (project_key / owner+repo).
      await connectMutation.mutateAsync({
        provider_key: providerId,
        display_name: prov?.label ?? providerId,
        config,
      });
    } finally {
      setConnectingProvider(null);
    }
  }

  async function handleSync(providerId) {
    const source = sourcesByProvider[providerId];
    if (!source?.id || !projectId) return;
    setSyncingSource(providerId);
    setSyncError(null);
    try {
      await api.post(`/projects/${projectId}/import-hub/${source.id}/sync`, {});
      queryClient.invalidateQueries({ queryKey: importKeys.items(projectId) });
      queryClient.invalidateQueries({ queryKey: importKeys.sources(projectId) });
    } catch (e) {
      setSyncError(e);
    } finally {
      setSyncingSource(null);
    }
  }

  // ── Error states ──────────────────────────────────────────────────────────

  if (sourcesError?.status === 403 || itemsError?.status === 403) {
    return <ForbiddenBanner />;
  }
  if (sourcesError?.status === 429 || itemsError?.status === 429) {
    return <RateLimitBanner />;
  }

  return (
    <div>
      {/* Cabeçalho */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Importação de Backlog
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          Opcional — importe itens de backlog de ferramentas externas. GitHub Issues e Jira
          conectam à API real; demais provedores estarão disponíveis em breve.
        </p>
      </div>

      {/* Aviso sobre etapa opcional */}
      <div
        style={{
          padding: '10px 14px',
          borderRadius: 6,
          backgroundColor: `${C.cyber}11`,
          border: `1px solid ${C.cyber}33`,
          fontSize: 13,
          color: C.neutralDim,
          marginBottom: 24,
          display: 'flex',
          alignItems: 'center',
          gap: 8,
        }}
        role="note"
      >
        <AlertCircle size={14} style={{ color: C.cyber, flexShrink: 0 }} aria-hidden="true" />
        <span>
          <strong style={{ color: C.cyber }}>Etapa opcional.</strong>{' '}
          Você pode pular e configurar o backlog diretamente no wizard. A importação
          sincroniza bidirecionalmente quando o projeto está ativo.
        </span>
      </div>

      {/* Provedores */}
      <Card style={{ marginBottom: 24 }}>
        <div style={{ marginBottom: 16 }}>
          <h3 style={{ fontSize: 14, fontWeight: 600, color: C.neutral, margin: '0 0 2px' }}>
            Provedores de Backlog
          </h3>
          <p style={{ fontSize: 12, color: C.neutralDim, margin: 0 }}>
            Conecte e sincronize fontes externas de trabalho.
          </p>
        </div>

        {sourcesLoading && !projectId && (
          <p style={{ fontSize: 13, color: C.neutralDim }}>
            Selecione um projeto para ver as fontes disponíveis.
          </p>
        )}
        {sourcesLoading && projectId && <LoadingSpinner label="Carregando fontes..." />}

        {!sourcesLoading && (
          <div
            style={{ display: 'flex', flexDirection: 'column', gap: 10 }}
            role="list"
            aria-label="Provedores de importação"
          >
            {PROVIDERS.map((prov) => (
              <ProviderCard
                key={prov.id}
                provider={prov}
                source={sourcesByProvider[prov.id] ?? null}
                onConnect={(config) => handleConnect(prov.id, config)}
                onSync={() => handleSync(prov.id)}
                connecting={connectingProvider === prov.id}
                syncing={syncingSource === prov.id}
                connectError={connectingProvider === prov.id ? connectMutation.error : null}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Erro de sincronização */}
      {syncError && (
        <div style={{ marginBottom: 16 }}>
          <MutationError error={syncError} onDismiss={() => setSyncError(null)} />
        </div>
      )}

      {/* Itens importados */}
      {totalItems > 0 && (
        <Card style={{ marginBottom: 24 }}>
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              marginBottom: 16,
            }}
          >
            <div>
              <h3 style={{ fontSize: 14, fontWeight: 600, color: C.neutral, margin: '0 0 4px' }}>
                Itens Importados
              </h3>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                {Object.entries(typeSummary).map(([type, count]) => (
                  <ItemTypePill key={type} type={type} count={count} />
                ))}
              </div>
            </div>
            <Badge color={C.cyber} light>
              {totalItems} {totalItems === 1 ? 'item' : 'itens'}
            </Badge>
          </div>

          {itemsLoading ? (
            <LoadingSpinner label="Carregando itens..." />
          ) : (
            <div
              style={{
                border: `1px solid ${C.blackBorder}`,
                borderRadius: 6,
                overflow: 'hidden',
                maxHeight: 320,
                overflowY: 'auto',
              }}
              aria-label="Lista de itens importados"
            >
              {importedItems.map((item, idx) => (
                <ImportedItemRow key={item.id ?? idx} item={item} />
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Estado vazio — sem itens ainda */}
      {!itemsLoading && totalItems === 0 && projectId && (
        <div
          style={{
            padding: '24px 20px',
            textAlign: 'center',
            backgroundColor: C.blackElev2,
            border: `1px dashed ${C.blackBorder}`,
            borderRadius: 8,
            marginBottom: 24,
            color: C.neutralDim,
            fontSize: 13,
          }}
          aria-live="polite"
        >
          Nenhum item importado ainda. Conecte um provedor acima e clique em{' '}
          <strong style={{ color: C.neutral }}>Sincronizar</strong> para trazer os itens.
        </div>
      )}

      {/* Rodapé de navegação */}
      <div className="flex items-center justify-between">
        <Btn variant="ghost" size="md" onClick={onBack}>
          Voltar
        </Btn>
        <div className="flex items-center gap-3">
          <Btn
            variant="ghost"
            size="md"
            onClick={onNext}
            aria-label="Pular etapa de importação"
          >
            Pular etapa
          </Btn>
          <Btn
            variant="primary"
            size="lg"
            icon={ArrowRight}
            onClick={onNext}
            aria-label="Continuar para a próxima etapa"
          >
            Continuar
          </Btn>
        </div>
      </div>
    </div>
  );
}
